import * as React from 'react';

import classnames from 'classnames';
import * as ramda from 'ramda';

import {useStableCallback} from '../../../../../ui/core/use-stable-callback';

import {colors} from '../colors/colors';

import {LoadingOverlay} from './loading-overlay';
import {MemoizedTableRows} from './memoized-table-rows';
import {TableRow} from './table-body/table-row';
import {TableFooterRow} from './table-footer/table-footer-row';
import {TableHeaderRow} from './table-header/table-header-row';
import {convertSortByToUrlSortParam} from './table-sort-helpers';
import type {HeaderGroup, Row, SortBy, SpecialState, VirtualizeProps} from './types';

import styles from './table-component.module.css';

export type TableVariationProps<T> = {
    hasBorders?: boolean;
    hasHeaderBorder?: boolean;
    isCompact?: boolean;
    hasLessBottomPadding?: boolean;
    /** Use the ResizableTable shell instead if you want resizable columns */
    isResizable?: boolean;
    isCheckboxSelectable?: boolean;
    defaultAllRowsChecked?: boolean;
    onCheckedRowsChange?: (selectedFlatRows: Row<T>[]) => void;
    hasCellBorders?: boolean;
    hasHeaderCellBorders?: boolean;
    hasHeaderTopBorder?: boolean;
    hasHeaderLeftBorder?: boolean;
    hasHeaderRightBorder?: boolean;
    hasFooterCellBorders?: boolean; // If the footer should have cell borders (matches other header borders),
    shouldShowFooter?: boolean;
    displayFooterAtTop?: boolean;
    stickyHeaders?: boolean;
    disableFilters?: boolean;
    disableSorting?: boolean;
    clickingSelectsRow?: boolean;
    hasHoverState?: boolean;
    headerBackgroundColor?: 'offWhite' | 'offWhiteDk';
    fullWidth?: boolean;
    extendToFitWidth?: boolean;
    hasHeaders?: boolean;
    state?: {
        sortBy: SortBy[];
    };
    sortBy?: SortBy[];
    renderRowActionMenu?: (row: Row<T>) => React.ReactElement;
    isStriped?: boolean;
    onSelectRow?: (row: Row<any>) => void;
    getRowRef?: (ref?: HTMLDivElement | null | undefined) => void;
    selectedRowId?: string | null | undefined;
    onMouseOver?: (row: Row<any>) => void;
    onMouseOut?: () => void;
    expandProps?: {
        renderExpandCell: (row: Row<T>) => React.ReactElement;
        renderSubComponent: (row: Row<T>) => React.ReactElement;
        expandRowIndex?: number | null | undefined;
        clearExpandRowIndex?: () => void;
        hideExpandCell?: boolean;
        matchTableWidth?: boolean;
        expandAllRows?: boolean;
    };
    virtualizeProps?: VirtualizeProps;
    // Variation contingencies
    shouldPreventSelectAll?: boolean;
    // If true the footer will follow the column types rightAlign and centerAlign props
    shouldFooterAlign?: boolean;
};

type Props<T> = TableVariationProps<T> & {
    // From react-table instance
    getTableProps: (...args: any) => any;
    getTableBodyProps: (...args: any) => any;
    getTableFooterProps: (...args: any) => any;
    prepareRow: (...args: any) => any;
    headerGroups: HeaderGroup[];
    footerGroups: HeaderGroup[];
    rows: Row<T>[];
    isLoading?: boolean;
    onChangeSortBy?: (newSortBy: SortBy[]) => void;
    getToggleAllRowsSelectedProps?: (...args: any) => any;
    specialStates?: SpecialState[];
    firstRow?: React.ReactElement<any> | null | undefined;
    secondRow?: React.ReactElement<any> | null | undefined;
    preserveHeaderInSpecialState?: boolean;
    disableSelectRowIds?: string[];
    selectedRowId?: string | null | undefined;
    getTheadRef?: (ref: HTMLTableSectionElement) => void;
};

export const findSpecialState = (states: SpecialState[]): SpecialState | null | undefined => {
    return ramda.find((state: SpecialState) => {
        return state.shouldRender();
    })(states);
};

const wrapTableRow = (component: React.ReactNode) => {
    return (
        <tr className={styles['special-row']}>
            <td>{component}</td>
        </tr>
    );
};

export function TableComponent<T>(props: Props<T>) {
    const {
        getTableProps,
        getTableBodyProps,
        getToggleAllRowsSelectedProps,
        headerGroups,
        footerGroups,
        shouldShowFooter,
        displayFooterAtTop,
        rows,
        prepareRow,
        isResizable,
        onChangeSortBy,
        sortBy,
        state: tableState,
        firstRow,
        secondRow,
        preserveHeaderInSpecialState,
        isStriped,
        fullWidth,
        extendToFitWidth,
        selectedRowId,
        hasHeaderRightBorder = true,
        hasHeaders = true,
        getRowRef,
    } = props;

    const theadRef = React.useRef(null);

    // Get our table's internal sortBy parameter
    let sortByState = null;
    if (tableState) {
        sortByState = tableState.sortBy;
    }

    // When our internal sort changes, if we've asked for a callback,
    // fire that with the new sort parameter. Only do this when the
    // new sort is different from the one passed to this component to
    // avoid a possible infinite loop
    React.useEffect(() => {
        if (onChangeSortBy && sortByState && sortBy) {
            const tableStateSortString = convertSortByToUrlSortParam(sortByState);
            const passedSortString = convertSortByToUrlSortParam(sortBy);

            if (tableStateSortString !== passedSortString) {
                onChangeSortBy(sortByState);
            }
        }
    }, [onChangeSortBy, sortByState, sortBy]);

    const getFooterTag = () => {
        if (isResizable) {
            return 'div';
        } else if (displayFooterAtTop) {
            return 'thead';
        } else {
            return 'tfoot';
        }
    };

    const TableElement = isResizable ? 'div' : 'table';
    const THead = isResizable ? React.Fragment : 'thead';
    const TBody = isResizable ? 'div' : 'tbody';
    const TFoot = getFooterTag();
    const specialState = props.specialStates ? findSpecialState(props.specialStates) : undefined;
    const Footer = shouldShowFooter ? (
        <TFoot {...getTableProps()}>
            {footerGroups.map((footerGroup, index) => (
                <TableFooterRow
                    key={index}
                    footerGroup={footerGroup}
                    isResizable={Boolean(isResizable)}
                    isCompact={Boolean(props.isCompact)}
                    hasCellBorders={Boolean(props.hasFooterCellBorders)}
                    hasBottomBorder={Boolean(props.hasHeaderBorder)}
                    hasLeftBorder={Boolean(props.hasHeaderLeftBorder)}
                    hasRightBorder={hasHeaderRightBorder}
                    extendToFitWidth={extendToFitWidth}
                    shouldFooterAlign={props.shouldFooterAlign}
                />
            ))}
        </TFoot>
    ) : undefined;

    const renderRow = useStableCallback((row: Row<T>) => {
        prepareRow(row);
        // @ts-expect-error
        const rowId = row.original && row.original.id ? row.original.id : undefined;

        const onSelectRow =
            rowId && props.disableSelectRowIds && props.disableSelectRowIds.includes(rowId)
                ? undefined
                : props.onSelectRow;

        const hasHoverState = Boolean(props.hasHoverState || onSelectRow);

        const isSelected = rowId && selectedRowId && rowId === selectedRowId;

        return (
            <TableRow
                key={row.index}
                row={row}
                isCompact={Boolean(props.isCompact)}
                hasLessBottomPadding={Boolean(props.hasLessBottomPadding)}
                isResizable={Boolean(isResizable)}
                isHoverable={hasHoverState}
                isSelectable={Boolean(props.clickingSelectsRow)}
                isSelected={Boolean(isSelected)}
                hasCellBorders={Boolean(props.hasCellBorders)}
                hasRowBorders={Boolean(props.hasBorders)}
                showCheckbox={Boolean(props.isCheckboxSelectable)}
                renderActionMenu={props.renderRowActionMenu}
                onSelectRow={onSelectRow}
                onMouseOver={props.onMouseOver}
                onMouseOut={props.onMouseOut}
                extendToFitWidth={extendToFitWidth}
                expandProps={props.expandProps}
                getRef={getRowRef}
            />
        );
    });

    // Used when we are virtualizing the table using FixedSizeList
    // @ts-expect-error
    const RenderFixedSizeListRows = ({index, style}) => {
        const row = rows[index];
        const isEvenRow = index % 2 === 0;
        const hasSingleBackgroundColor =
            props.virtualizeProps && props.virtualizeProps.hasSingleBackgroundColor;

        return (
            <div
                style={{
                    ...style,
                    backgroundColor:
                        isEvenRow || hasSingleBackgroundColor ? colors.white : colors.offWhite,
                }}
            >
                {renderRow(row)}
            </div>
        );
    };

    // If we have a first row, we need to show it and then the special state below
    const displaySpecialStateBelowTableContent = specialState && firstRow;

    const styleName = classnames({
        [styles.table]: !isStriped,
        [styles['table--striped']]: isStriped,
        [styles['full-width']]: fullWidth || extendToFitWidth,
    });

    const shouldShowOnlySpecialState =
        specialState && !displaySpecialStateBelowTableContent && !preserveHeaderInSpecialState;

    const shouldShowTableRows = !specialState || displaySpecialStateBelowTableContent;

    React.useEffect(() => {
        if (props.getTheadRef && theadRef.current) {
            props.getTheadRef(theadRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.getTheadRef, theadRef.current]);

    return (
        <>
            {shouldShowOnlySpecialState ? (
                specialState && specialState.component
            ) : (
                <TableElement {...getTableProps()} className={styleName}>
                    {(rows.length || preserveHeaderInSpecialState) && hasHeaders && (
                        <THead ref={theadRef}>
                            {headerGroups.map((headerGroup, index) => (
                                <TableHeaderRow
                                    key={index}
                                    headerGroup={headerGroup}
                                    isResizable={Boolean(isResizable)}
                                    isCompact={Boolean(props.isCompact)}
                                    isSticky={Boolean(props.stickyHeaders)}
                                    tableHasActionMenus={Boolean(props.renderRowActionMenu)}
                                    tableHasExpandCell={Boolean(
                                        props.expandProps && !props.expandProps.hideExpandCell
                                    )}
                                    // @ts-expect-error
                                    getToggleAllRowsSelectedProps={getToggleAllRowsSelectedProps}
                                    isCheckboxSelectable={Boolean(props.isCheckboxSelectable)}
                                    shouldPreventSelectAll={Boolean(props.shouldPreventSelectAll)}
                                    hasCellBorders={Boolean(props.hasHeaderCellBorders)}
                                    hasBottomBorder={Boolean(props.hasHeaderBorder)}
                                    hasTopBorder={Boolean(props.hasHeaderTopBorder)}
                                    hasLeftBorder={Boolean(props.hasHeaderLeftBorder)}
                                    hasRightBorder={hasHeaderRightBorder}
                                    headerBackgroundColor={props.headerBackgroundColor}
                                    extendToFitWidth={props.extendToFitWidth}
                                />
                            ))}
                        </THead>
                    )}
                    {Footer && displayFooterAtTop ? Footer : undefined}
                    <TBody {...getTableBodyProps()}>
                        {firstRow && wrapTableRow(firstRow)}
                        {secondRow && wrapTableRow(secondRow)}
                        {shouldShowTableRows && (
                            <MemoizedTableRows
                                rows={rows}
                                renderRow={renderRow}
                                renderFixedSizeListRows={RenderFixedSizeListRows}
                                virtualizeProps={props.virtualizeProps}
                                headerGroups={headerGroups}
                                expandProps={props.expandProps}
                                specialStates={props.specialStates}
                            />
                        )}
                        {specialState && wrapTableRow(specialState.component)}
                        {props.isLoading && wrapTableRow(<LoadingOverlay />)}
                    </TBody>
                    {Footer && !displayFooterAtTop ? Footer : undefined}
                </TableElement>
            )}
        </>
    );
}
